/**
 * Roster row rules (pure — tested in staffRowRules.test.tsx).
 *
 * The Owner now has a real staff row (R22, role_owner) — it must stay as
 * untouchable in the UI as the old unmanaged Cognito-only entry was: no
 * detail page, no onboarding, no suspend/delete/role change, no Reset MFA.
 * The backend refuses all of these anyway; this keeps them out of reach.
 */
import type { StaffMember } from '../../services/staffApi';

type Row = Pick<StaffMember, 'id' | 'email' | 'status' | 'unmanaged' | 'isOwner'>;

export const isLockedMember = (m: Partial<Row>): boolean => !!m.unmanaged || !!m.isOwner;

export const canResetMfaFor = (m: Partial<Row>, myEmail: string | null | undefined): boolean =>
  !!m.id && !m.isOwner && m.status !== 'invited' && m.status !== 'terminated'
  && (m.email || '').toLowerCase() !== (myEmail || '').toLowerCase();

/**
 * Send the invite, then refresh the roster. Only the INVITE can fail the
 * modal: once it succeeded the person is invited, so a failed refresh just
 * closes the modal with a "refresh to see it" note — never an invite error.
 */
export async function inviteThenRefresh(o: {
  invite: () => Promise<unknown>;
  refresh: () => Promise<unknown>;
  close: () => void;
  success: (msg: string) => void;
  info: (msg: string) => void;
  email: string;
}): Promise<void> {
  await o.invite();
  o.close();
  try {
    await o.refresh();
  } catch {
    o.info('Invite sent — refresh to see it');
    return;
  }
  // Cognito emails the temporary password itself — nothing to copy or share.
  o.success(`Invitation sent — ${o.email} will receive a temporary password by email`);
}
